import { useEffect, useState } from "react";
import axios from "axios";
import {
  LogIn,
  Trash2,
  RefreshCw,
  Phone,
  MessageCircle,
  Plus,
  Pencil,
  Eye,
  EyeOff,
  X,
  Star,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { LOGO } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blankReview = {
  name: "",
  role: "",
  branch: "",
  rating: 5,
  message: "",
  is_published: true,
};

const inputCls =
  "bg-[#0F0F0F] border-white/10 text-white placeholder:text-white/30 h-11 rounded-sm focus-visible:ring-[#FACC15]";

export default function Admin() {
  const [token, setToken] = useState(
    () => localStorage.getItem("justudy_admin_token") || ""
  );
  const [pwd, setPwd] = useState("");
  const [contacts, setContacts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(blankReview);

  const authHeader = { headers: { "X-Admin-Token": token } };

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { token: pwd });
      setToken(res.data.token);
      localStorage.setItem("justudy_admin_token", res.data.token);
      toast.success("Logged in");
    } catch {
      toast.error("Invalid token");
    }
  };

  const logout = () => {
    setToken("");
    localStorage.removeItem("justudy_admin_token");
  };

  const fetchAll = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        axios.get(`${API}/admin/contacts`, authHeader),
        axios.get(`${API}/admin/reviews`, authHeader),
      ]);
      setContacts(c.data);
      setReviews(r.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired");
        logout();
      } else {
        toast.error("Failed to load");
      }
    } finally {
      setLoading(false);
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, authHeader);
      setContacts((c) => c.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const openNewReview = () => {
    setDraft(blankReview);
    setEditing({});
  };
  const openEditReview = (r) => {
    setDraft({
      name: r.name || "",
      role: r.role || "",
      branch: r.branch || "",
      rating: r.rating || 5,
      message: r.message || "",
      is_published: r.is_published !== false,
    });
    setEditing(r);
  };
  const closeEditor = () => {
    setEditing(null);
    setDraft(blankReview);
  };

  const saveReview = async () => {
    if (!draft.name.trim() || !draft.message.trim()) {
      toast.error("Name and message are required");
      return;
    }
    try {
      if (editing && editing.id) {
        const res = await axios.put(
          `${API}/admin/reviews/${editing.id}`,
          draft,
          authHeader
        );
        setReviews((rs) => rs.map((x) => (x.id === editing.id ? res.data : x)));
        toast.success("Review updated");
      } else {
        const res = await axios.post(`${API}/admin/reviews`, draft, authHeader);
        setReviews((rs) => [res.data, ...rs]);
        toast.success("Review added");
      }
      closeEditor();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, authHeader);
      setReviews((rs) => rs.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const togglePublish = async (r) => {
    try {
      const res = await axios.put(
        `${API}/admin/reviews/${r.id}`,
        { is_published: !r.is_published },
        authHeader
      );
      setReviews((rs) => rs.map((x) => (x.id === r.id ? res.data : x)));
    } catch {
      toast.error("Update failed");
    }
  };

  useEffect(() => {
    if (token) fetchAll();
    // eslint-disable-next-line
  }, [token]);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] grid place-items-center px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm bg-[#1A1A1A] border border-white/10 rounded-sm p-8"
        >
          <div className="w-14 h-14 rounded-md bg-white grid place-items-center overflow-hidden mb-5">
            <img src={LOGO} alt="Justudy" className="w-full h-full object-contain" />
          </div>
          <h1 className="font-display text-2xl font-semibold text-white mb-1">
            Admin Access
          </h1>
          <p className="text-sm text-white/50 mb-6">
            Enter your admin token to continue.
          </p>
          <Input
            type="password"
            data-testid="admin-token-input"
            placeholder="Admin token"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            className={inputCls + " h-12"}
          />
          <button
            type="submit"
            data-testid="admin-login-btn"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#FACC15] hover:bg-[#EAB308] text-[#0F0F0F] font-semibold px-5 py-3 rounded-sm"
          >
            <LogIn className="w-4 h-4" /> Sign in
          </button>
          <a
            href="/"
            className="block mt-4 text-center text-xs text-white/40 hover:text-[#FACC15]"
          >
            ← Back to website
          </a>
        </form>
        <Toaster theme="dark" position="top-right" richColors />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0F0F] text-white">
      <header className="border-b border-white/10 bg-[#0B0B0B]">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-white grid place-items-center overflow-hidden">
              <img src={LOGO} alt="Justudy" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-display font-semibold">Justudy Admin</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#FACC15] font-semibold">
                Dashboard
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchAll}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 text-sm border border-white/15 hover:border-[#FACC15] hover:text-[#FACC15] px-3 py-2 rounded-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="text-sm text-white/60 hover:text-white px-3 py-2"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <Tabs defaultValue="reviews">
          <TabsList className="bg-transparent border-b border-white/10 rounded-none w-full justify-start gap-0 p-0 h-auto mb-8">
            <TabsTrigger
              value="reviews"
              data-testid="admin-tab-reviews"
              className="bg-transparent rounded-none px-5 py-3 text-sm font-medium text-white/50 data-[state=active]:bg-transparent data-[state=active]:text-[#FACC15] data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#FACC15] -mb-px"
            >
              Reviews ({reviews.length})
            </TabsTrigger>
            <TabsTrigger
              value="contacts"
              data-testid="admin-tab-contacts"
              className="bg-transparent rounded-none px-5 py-3 text-sm font-medium text-white/50 data-[state=active]:bg-transparent data-[state=active]:text-[#FACC15] data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#FACC15] -mb-px"
            >
              Submissions ({contacts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reviews">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl md:text-3xl font-semibold">
                Manage Reviews
              </h2>
              <button
                onClick={openNewReview}
                data-testid="admin-add-review"
                className="inline-flex items-center gap-2 bg-[#FACC15] hover:bg-[#EAB308] text-[#0F0F0F] text-sm font-semibold px-4 py-2.5 rounded-sm"
              >
                <Plus className="w-4 h-4" /> Add Review
              </button>
            </div>

            {reviews.length === 0 && (
              <div className="text-center py-16 text-white/40 border border-dashed border-white/10 rounded-sm">
                No reviews yet.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {reviews.map((r) => (
                <div
                  key={r.id}
                  data-testid={`admin-review-${r.id}`}
                  className={`bg-[#1A1A1A] border ${
                    r.is_published
                      ? "border-white/5"
                      : "border-red-500/30 opacity-70"
                  } rounded-sm p-5`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h4 className="font-display font-semibold text-white">
                          {r.name}
                        </h4>
                        <div className="flex gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              className={`w-3.5 h-3.5 ${
                                i < r.rating
                                  ? "fill-[#FACC15] text-[#FACC15]"
                                  : "text-white/15"
                              }`}
                            />
                          ))}
                        </div>
                        {r.branch && (
                          <span className="text-[10px] uppercase tracking-[0.15em] text-[#FACC15] border border-[#FACC15]/30 px-2 py-0.5 rounded-sm">
                            {r.branch}
                          </span>
                        )}
                      </div>
                      {r.role && (
                        <div className="text-xs text-white/40 mt-1">{r.role}</div>
                      )}
                      <p className="text-sm text-white/75 mt-3 leading-relaxed">
                        {r.message}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => togglePublish(r)}
                      className="inline-flex items-center gap-1.5 text-xs border border-white/10 hover:border-[#FACC15] hover:text-[#FACC15] px-2.5 py-1.5 rounded-sm"
                    >
                      {r.is_published ? (
                        <>
                          <Eye className="w-3 h-3" /> Published
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-3 h-3" /> Hidden
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => openEditReview(r)}
                      className="inline-flex items-center gap-1.5 text-xs border border-white/10 hover:border-[#FACC15] hover:text-[#FACC15] px-2.5 py-1.5 rounded-sm"
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <button
                      onClick={() => deleteReview(r.id)}
                      className="inline-flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 border border-red-400/20 hover:border-red-400/50 px-2.5 py-1.5 rounded-sm"
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="contacts">
            <h2 className="font-display text-2xl md:text-3xl font-semibold mb-5">
              Contact Submissions
            </h2>
            {contacts.length === 0 && (
              <div className="text-center py-16 text-white/40 border border-dashed border-white/10 rounded-sm">
                No submissions yet.
              </div>
            )}
            <div className="space-y-3">
              {contacts.map((c) => (
                <div
                  key={c.id}
                  data-testid={`admin-contact-${c.id}`}
                  className="bg-[#1A1A1A] border border-white/5 rounded-sm p-5 md:p-6"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-display text-lg font-semibold text-white">
                          {c.name}
                        </h3>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#FACC15] border border-[#FACC15]/30 px-2 py-0.5 rounded-sm">
                          {c.branch}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-white/40">
                        {new Date(c.created_at).toLocaleString()}
                      </div>
                      <p className="mt-3 text-sm text-white/75 leading-relaxed">
                        {c.message}
                      </p>
                    </div>
                    <div className="flex flex-row md:flex-col gap-2 md:items-end">
                      <a
                        href={`tel:${c.phone}`}
                        className="inline-flex items-center gap-2 text-xs bg-[#FACC15] text-[#0F0F0F] font-semibold px-3 py-2 rounded-sm"
                      >
                        <Phone className="w-3.5 h-3.5" /> {c.phone}
                      </a>
                      <a
                        href={`https://wa.me/${c.phone.replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-xs bg-[#25D366] text-white font-semibold px-3 py-2 rounded-sm"
                      >
                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                      </a>
                      <button
                        onClick={() => deleteContact(c.id)}
                        className="inline-flex items-center gap-2 text-xs text-red-400 hover:text-red-300 px-3 py-2 border border-red-400/20 hover:border-red-400/50 rounded-sm"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center px-6">
          <div className="w-full max-w-lg bg-[#1A1A1A] border border-white/10 rounded-sm p-6 md:p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-semibold">
                {editing.id ? "Edit Review" : "Add Review"}
              </h3>
              <button onClick={closeEditor} className="text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <Input
                placeholder="Student name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className={inputCls}
              />
              <Input
                placeholder="Role / Tag (e.g. UPSC Aspirant)"
                value={draft.role}
                onChange={(e) => setDraft({ ...draft, role: e.target.value })}
                className={inputCls}
              />
              <select
                value={draft.branch}
                onChange={(e) => setDraft({ ...draft, branch: e.target.value })}
                className="w-full bg-[#0F0F0F] border border-white/10 text-white h-11 rounded-sm px-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#FACC15]"
              >
                <option value="">Branch (optional)</option>
                <option value="Sector 22 Noida">Sector 22 Noida</option>
                <option value="Sector 26 Noida">Sector 26 Noida</option>
              </select>
              <div>
                <div className="text-xs text-white/50 uppercase tracking-[0.15em] mb-2 font-semibold">
                  Rating
                </div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setDraft({ ...draft, rating: n })}
                      className="p-1"
                    >
                      <Star
                        className={`w-6 h-6 ${
                          n <= draft.rating
                            ? "fill-[#FACC15] text-[#FACC15]"
                            : "text-white/20"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <Textarea
                placeholder="Review message"
                rows={5}
                value={draft.message}
                onChange={(e) => setDraft({ ...draft, message: e.target.value })}
                className="bg-[#0F0F0F] border-white/10 text-white placeholder:text-white/30 rounded-sm focus-visible:ring-[#FACC15]"
              />
              <label className="flex items-center gap-2 text-sm text-white/70">
                <input
                  type="checkbox"
                  checked={draft.is_published}
                  onChange={(e) =>
                    setDraft({ ...draft, is_published: e.target.checked })
                  }
                  className="accent-[#FACC15]"
                />
                Publish on website
              </label>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={closeEditor}
                className="text-sm text-white/60 hover:text-white px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={saveReview}
                data-testid="admin-save-review"
                className="inline-flex items-center gap-2 bg-[#FACC15] hover:bg-[#EAB308] text-[#0F0F0F] font-semibold text-sm px-4 py-2.5 rounded-sm"
              >
                Save Review
              </button>
            </div>
          </div>
        </div>
      )}

      <Toaster theme="dark" position="top-right" richColors />
    </div>
  );
}
